import React, { useState, useEffect } from "react";
import { GoogleMap, LoadScript, Marker, InfoWindow, MarkerClusterer } from "@react-google-maps/api";
import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import { FaMapMarkedAlt, FaListUl } from "react-icons/fa";

const RequestDataMap = () => {
  const [requestData, setRequestData] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 51.1657, lng: 10.4515 });
  const [mapZoom, setMapZoom] = useState(6);
  const [companyName, setCompanyName] = useState("");
  const [activeTab, setActiveTab] = useState("karte");
  const [loadError, setLoadError] = useState("");

  const mapStyle = [
    { elementType: "geometry", stylers: [{ visibility: "simplified" }] },
    { elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "locality", elementType: "labels", stylers: [{ visibility: "on" }] },
    { featureType: "landscape", stylers: [{ visibility: "off" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "road", stylers: [{ visibility: "off" }] },
    { featureType: "water", stylers: [{ visibility: "off" }] },
    { featureType: "administrative", stylers: [{ visibility: "on" }] },
  ];

  useEffect(() => {
    UserService.getRequestData().then(
      (response) => {
        const data = response.data || [];
        setRequestData(data);
        if (data.length > 0 && data[0].t_batch_codes && data[0].t_batch_codes.t_customer) {
          setCompanyName(data[0].t_batch_codes.t_customer.custumer_company_name);
        }
        calculateOptimalCenterAndZoom(data);
      },
      (error) => {
        setRequestData([]);
        if (error.response?.status === 401) {
          EventBus.dispatch("logout");
        } else {
          setLoadError("Die Klickorte konnten nicht geladen werden.");
        }
      }
    );
  }, []);

  const calculateOptimalCenterAndZoom = (data) => {
    const validLocations = (data || []).reduce((acc, item) => {
      const lat = parseFloat(item.latitude);
      const lng = parseFloat(item.longitude);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) acc.push({ lat, lng });
      return acc;
    }, []);
    if (!validLocations.length) {
      setMapCenter({ lat: 51.1657, lng: 10.4515 });
      setMapZoom(6);
      return;
    }
    const latitudes = validLocations.map((loc) => loc.lat);
    const longitudes = validLocations.map((loc) => loc.lng);
    setMapCenter({
      lat: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
      lng: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
    });
    setMapZoom(6);
  };

  const placeLabel = (item) => [item.city, item.region, item.country].filter(Boolean).join(", ") || "ohne Ortsangabe";

  return (
    <section className="myn-sheet">
      <header className="myn-sheet__head">
        <span className="myn-sheet__icon" aria-hidden="true"><FaMapMarkedAlt /></span>
        <div>
          <p className="myn-sheet__kicker">Karte</p>
          <h1 className="myn-sheet__title">Klickverwaltung</h1>
          <p className="myn-sheet__lead">
            {companyName ? `Klickorte für ${companyName}. ` : ""}
            Die Zahlenauswertung liegt unter Meine Klicks und Klicks meiner Mitarbeiter. Hier sehen Sie nur den Ort der Klicks.
          </p>
        </div>
      </header>
      <div className="myn-sheet__tabs" role="tablist" aria-label="Klickverwaltung">
        <button type="button" role="tab" aria-selected={activeTab === "karte"} className={activeTab === "karte" ? "myn-sheet__tab is-active" : "myn-sheet__tab"} onClick={() => setActiveTab("karte")}>
          <FaMapMarkedAlt aria-hidden="true" /><span>Karte</span>
        </button>
        <button type="button" role="tab" aria-selected={activeTab === "liste"} className={activeTab === "liste" ? "myn-sheet__tab is-active" : "myn-sheet__tab"} onClick={() => setActiveTab("liste")}>
          <FaListUl aria-hidden="true" /><span>Liste</span>
        </button>
      </div>
      {loadError && <p>{loadError}</p>}

      {activeTab === "karte" && (
        <div className="myn-sheet__panel">
          <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              mapContainerClassName="myn-map"
              center={mapCenter}
              zoom={mapZoom}
              options={{ styles: mapStyle }}
            >
              <MarkerClusterer>
                {(clusterer) => requestData.map((item, index) => {
                  const lat = parseFloat(item.latitude);
                  const lng = parseFloat(item.longitude);
                  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
                  const offset = 0.0001 * index;
                  return (
                    <Marker
                      key={`${item.ip_address || "ort"}-${index}`}
                      clusterer={clusterer}
                      position={{ lat: lat + offset, lng: lng + offset }}
                      label={{ text: `${item.clicks || 0}`, color: "#1f2d3d", fontSize: "12px" }}
                      onClick={() => setSelectedMarker(item)}
                    />
                  );
                })}
              </MarkerClusterer>
              {selectedMarker && (
                <InfoWindow
                  position={{ lat: parseFloat(selectedMarker.latitude), lng: parseFloat(selectedMarker.longitude) }}
                  onCloseClick={() => setSelectedMarker(null)}
                >
                  <div className="myn-map-popup">
                    <strong>{placeLabel(selectedMarker)}</strong>
                    <p>Klicks: {selectedMarker.clicks}</p>
                    <p>Mitarbeiter: {selectedMarker?.t_batch_codes?.t_employee?.employees_firstname || "Inhaber"}</p>
                    <p>Chargennummer: {selectedMarker.batch_id || "keine Angabe"}</p>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </LoadScript>
        </div>
      )}

      {activeTab === "liste" && (
        <div className="myn-sheet__stack">
          {requestData.length === 0 && <p>Keine Klickorte vorhanden.</p>}
          {requestData.map((item, index) => (
            <article className="myn-sheet__review" key={`${item.ip_address || "ort"}-${index}`}>
              <h2>{placeLabel(item)}</h2>
              <div className="myn-sheet__grid">
                <p className="myn-sheet__link"><span>Klicks</span><strong>{item.clicks || 0}</strong></p>
                <p className="myn-sheet__link"><span>Mitarbeiter</span><strong>{item?.t_batch_codes?.t_employee?.employees_firstname || "Inhaber"}</strong></p>
                <p className="myn-sheet__link"><span>Status</span><strong>{item.status || "keine Angabe"}</strong></p>
                <p className="myn-sheet__link"><span>Chargennummer</span><strong>{item.batch_id || "keine Angabe"}</strong></p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default RequestDataMap;
